import { supabase } from './supabase'
import type { PriceMode } from '../utils/subscription'

/**
 * Abonelikler (1 Kas 2026): kurumların fiyat türü ve aldığı ödemeler,
 * supabase/abonelikler.sql. Yalnız sistem yöneticisi okuyup yazabiliyor.
 */

export type PayMethod = 'havale' | 'nakit' | 'kart' | 'diger'

export const METHOD_LABEL: Record<PayMethod, string> = {
  havale: 'Havale / EFT',
  nakit: 'Nakit',
  kart: 'Kart',
  diger: 'Diğer',
}

export interface Subscription {
  tenantId: string
  mode: PriceMode
  /** Özel fiyat, kurumun dönemi başına, kuruş. */
  customKurus: number | null
  /** Özel fiyatın bittiği gün; boşsa süresiz. */
  customUntil: string | null
  note: string
}

export interface SubPayment {
  id: string
  tenantId: string
  amountKurus: number
  method: PayMethod
  paidOn: string
  coversFrom: string
  coversTo: string
  note: string
  receiptPath: string | null
  createdAt: string
}

const DEKONT = 'dekontlar'

function db() {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')
  return supabase
}

export async function listSubscriptions(): Promise<Record<string, Subscription>> {
  const { data, error } = await db().from('subscriptions').select('tenant_id, price_mode, custom_kurus, custom_until, note')
  if (error) throw new Error(error.message)
  const out: Record<string, Subscription> = {}
  for (const r of data ?? []) {
    out[r.tenant_id] = {
      tenantId: r.tenant_id,
      mode: r.price_mode,
      customKurus: r.custom_kurus,
      customUntil: r.custom_until ?? null,
      note: r.note ?? '',
    }
  }
  return out
}

export async function saveSubscription(s: Subscription): Promise<void> {
  const { error } = await db()
    .from('subscriptions')
    .upsert({
      tenant_id: s.tenantId,
      price_mode: s.mode,
      custom_kurus: s.mode === 'custom' ? s.customKurus : null,
      custom_until: s.mode === 'custom' ? s.customUntil : null,
      note: s.note,
      updated_at: new Date().toISOString(),
    })
  if (error) throw new Error(error.message)
}

export async function listPayments(): Promise<SubPayment[]> {
  const { data, error } = await db()
    .from('subscription_payments')
    .select('id, tenant_id, amount_kurus, method, paid_on, covers_from, covers_to, note, receipt_path, created_at')
    .order('covers_to', { ascending: false })
  if (error) throw new Error(error.message)
  return (data ?? []).map((r) => ({
    id: r.id,
    tenantId: r.tenant_id,
    amountKurus: r.amount_kurus,
    method: r.method,
    paidOn: r.paid_on,
    coversFrom: r.covers_from,
    coversTo: r.covers_to,
    note: r.note ?? '',
    receiptPath: r.receipt_path,
    createdAt: r.created_at,
  }))
}

export type NewPayment = Omit<SubPayment, 'id' | 'receiptPath' | 'createdAt'>

/**
 * Ödemeyi kaydeder, dekont varsa yükler. Dekont yüklenemezse ödeme yine
 * kayıtlı kalıyor (para alınmış, kanıt sonradan eklenebilir); hata metni
 * dönüyor ki ekranda söylensin.
 */
export async function addPayment(p: NewPayment, file: File | null): Promise<{ receiptError: string | null }> {
  const { data, error } = await db()
    .from('subscription_payments')
    .insert({
      tenant_id: p.tenantId,
      amount_kurus: p.amountKurus,
      method: p.method,
      paid_on: p.paidOn,
      covers_from: p.coversFrom,
      covers_to: p.coversTo,
      note: p.note,
    })
    .select('id')
    .single()
  if (error) throw new Error(error.message)
  if (!file) return { receiptError: null }
  try {
    await attachReceipt(p.tenantId, data.id, file)
    return { receiptError: null }
  } catch (err) {
    return { receiptError: err instanceof Error ? err.message : 'Dekont yüklenemedi' }
  }
}

/** Kayıtlı bir ödemeye dekont yükler (ödeme alırken ya da sonradan). */
export async function attachReceipt(tenantId: string, paymentId: string, file: File): Promise<void> {
  const ext = (file.name.split('.').pop() || 'pdf').toLowerCase().replace(/[^a-z0-9]/g, '')
  const path = `${tenantId}/${paymentId}.${ext}`
  const up = await db().storage.from(DEKONT).upload(path, file, { contentType: file.type || undefined })
  if (up.error) throw new Error(up.error.message)
  const { error } = await db().from('subscription_payments').update({ receipt_path: path }).eq('id', paymentId)
  if (error) throw new Error(error.message)
}

export async function deletePayment(p: SubPayment): Promise<void> {
  if (p.receiptPath) await db().storage.from(DEKONT).remove([p.receiptPath])
  const { error } = await db().from('subscription_payments').delete().eq('id', p.id)
  if (error) throw new Error(error.message)
}

/**
 * Dekontun kısa süreli bağlantısı (kova gizli). download verilirse tarayıcı
 * dosyayı açmak yerine indirir.
 */
export async function receiptUrl(path: string, download = false): Promise<string> {
  const name = path.split('/').pop() || 'dekont'
  const { data, error } = await db()
    .storage.from(DEKONT)
    .createSignedUrl(path, 120, download ? { download: name } : undefined)
  if (error || !data) throw new Error(error?.message ?? 'Dekont açılamadı')
  return data.signedUrl
}
